/**
 * Banco de PALABRAS SECRETAS del juego "Palabra secreta" (`describe-it`).
 *
 * Regla dura del juego: la palabra vive SOLO en el cliente — se muestra en
 * pantalla y JAMÁS viaja al backend (el `session.update` manda únicamente
 * `{ seed: 'describe-it' }`). Así el coach NO la conoce y la adivina en serio;
 * esa asimetría es el juego. No agregar la palabra a ningún payload.
 *
 * Curaduría: sustantivos DESCRIBIBLES (uso, lugar, aspecto) escalonados por la
 * intensidad efectiva del alumno. La traducción ES acompaña siempre — el
 * objetivo es practicar circumlocución (describir alrededor), no adivinar qué
 * significa la palabra que te tocó.
 */

import type { EnglishIntensity } from '@orborix/shared-types';

export interface SecretWord {
  en: string;
  es: string;
}

type SecretWordBand = 'basic' | 'everyday' | 'independent' | 'advanced';

const BAND_BY_INTENSITY: Record<EnglishIntensity, SecretWordBand> = {
  starter: 'basic',
  cozy: 'everyday',
  natural: 'independent',
  push: 'advanced',
  stretch: 'advanced',
  native: 'advanced',
};

const SECRET_WORDS: Record<SecretWordBand, readonly SecretWord[]> = {
  basic: [
    { en: 'dog', es: 'perro' },
    { en: 'cat', es: 'gato' },
    { en: 'house', es: 'casa' },
    { en: 'water', es: 'agua' },
    { en: 'milk', es: 'leche' },
    { en: 'bread', es: 'pan' },
    { en: 'apple', es: 'manzana' },
    { en: 'chair', es: 'silla' },
    { en: 'table', es: 'mesa' },
    { en: 'bed', es: 'cama' },
    { en: 'door', es: 'puerta' },
    { en: 'window', es: 'ventana' },
    { en: 'car', es: 'auto' },
    { en: 'bus', es: 'colectivo' },
    { en: 'book', es: 'libro' },
    { en: 'shoe', es: 'zapato' },
    { en: 'hat', es: 'sombrero' },
    { en: 'sun', es: 'sol' },
    { en: 'moon', es: 'luna' },
    { en: 'rain', es: 'lluvia' },
    { en: 'egg', es: 'huevo' },
    { en: 'fish', es: 'pez' },
    { en: 'tree', es: 'árbol' },
    { en: 'phone', es: 'teléfono' },
  ],
  everyday: [
    { en: 'umbrella', es: 'paraguas' },
    { en: 'kitchen', es: 'cocina' },
    { en: 'birthday', es: 'cumpleaños' },
    { en: 'breakfast', es: 'desayuno' },
    { en: 'bicycle', es: 'bicicleta' },
    { en: 'doctor', es: 'médico' },
    { en: 'hospital', es: 'hospital' },
    { en: 'airport', es: 'aeropuerto' },
    { en: 'ticket', es: 'entrada / pasaje' },
    { en: 'wallet', es: 'billetera' },
    { en: 'mirror', es: 'espejo' },
    { en: 'pillow', es: 'almohada' },
    { en: 'blanket', es: 'frazada' },
    { en: 'scissors', es: 'tijeras' },
    { en: 'toothbrush', es: 'cepillo de dientes' },
    { en: 'ladder', es: 'escalera (de mano)' },
    { en: 'bridge', es: 'puente' },
    { en: 'island', es: 'isla' },
    { en: 'waiter', es: 'mozo' },
    { en: 'cheese', es: 'queso' },
    { en: 'butterfly', es: 'mariposa' },
    { en: 'spider', es: 'araña' },
    { en: 'helmet', es: 'casco' },
    { en: 'backpack', es: 'mochila' },
    { en: 'candle', es: 'vela' },
    { en: 'pocket', es: 'bolsillo' },
    { en: 'towel', es: 'toalla' },
    { en: 'key', es: 'llave' },
  ],
  independent: [
    { en: 'neighbor', es: 'vecino' },
    { en: 'luggage', es: 'equipaje' },
    { en: 'recipe', es: 'receta' },
    { en: 'schedule', es: 'horario / agenda' },
    { en: 'receipt', es: 'ticket / recibo' },
    { en: 'passport', es: 'pasaporte' },
    { en: 'elevator', es: 'ascensor' },
    { en: 'microwave', es: 'microondas' },
    { en: 'drawer', es: 'cajón' },
    { en: 'curtain', es: 'cortina' },
    { en: 'ceiling', es: 'techo (interior)' },
    { en: 'basement', es: 'sótano' },
    { en: 'landlord', es: 'dueño / casero' },
    { en: 'traffic jam', es: 'embotellamiento' },
    { en: 'seatbelt', es: 'cinturón de seguridad' },
    { en: 'screwdriver', es: 'destornillador' },
    { en: 'flashlight', es: 'linterna' },
    { en: 'envelope', es: 'sobre (de carta)' },
    { en: 'charger', es: 'cargador' },
    { en: 'headphones', es: 'auriculares' },
    { en: 'lighthouse', es: 'faro' },
    { en: 'greenhouse', es: 'invernadero' },
    { en: 'windmill', es: 'molino de viento' },
    { en: 'compass', es: 'brújula' },
    { en: 'shortcut', es: 'atajo' },
    { en: 'stain', es: 'mancha' },
    { en: 'crumbs', es: 'migas' },
    { en: 'queue', es: 'fila / cola' },
  ],
  advanced: [
    { en: 'ambition', es: 'ambición' },
    { en: 'rumor', es: 'rumor' },
    { en: 'deadline', es: 'fecha límite' },
    { en: 'compromise', es: 'acuerdo mutuo' },
    { en: 'jealousy', es: 'celos' },
    { en: 'courage', es: 'coraje' },
    { en: 'excuse', es: 'excusa' },
    { en: 'revenge', es: 'venganza' },
    { en: 'nostalgia', es: 'nostalgia' },
    { en: 'willpower', es: 'fuerza de voluntad' },
    { en: 'procrastination', es: 'procrastinación' },
    { en: 'coincidence', es: 'coincidencia' },
    { en: 'hiccup', es: 'hipo' },
    { en: 'yawn', es: 'bostezo' },
    { en: 'shadow', es: 'sombra' },
    { en: 'echo', es: 'eco' },
    { en: 'riddle', es: 'adivinanza' },
    { en: 'bargain', es: 'ganga' },
    { en: 'grudge', es: 'rencor' },
    { en: 'milestone', es: 'hito' },
    { en: 'loophole', es: 'vacío legal' },
    { en: 'hunch', es: 'corazonada' },
    { en: 'eavesdropping', es: 'escuchar a escondidas' },
    { en: 'small talk', es: 'charla trivial' },
    { en: 'peer pressure', es: 'presión de grupo' },
    { en: 'wishful thinking', es: 'ilusión / expresión de deseo' },
    { en: 'daydream', es: 'soñar despierto' },
    { en: 'gossip', es: 'chisme' },
  ],
};

/**
 * Elige una palabra secreta al azar para la banda de la intensidad dada,
 * evitando las recién usadas (`recentEn`, en inglés). Si la exclusión vacía el
 * pool (muchas rondas seguidas), cae al pool completo de la banda — repetir
 * tarde es mejor que no jugar. `random` inyectable para tests.
 */
export function pickSecretWord(
  intensity: EnglishIntensity,
  recentEn: readonly string[] = [],
  random: () => number = Math.random,
): SecretWord {
  const band = SECRET_WORDS[BAND_BY_INTENSITY[intensity]];
  const available = band.filter((w) => !recentEn.includes(w.en));
  const pool = available.length > 0 ? available : band;
  return pool[Math.floor(random() * pool.length)]!;
}

/** Exportado sólo para tests (cobertura del banco). */
export const SECRET_WORD_BANDS = SECRET_WORDS;
